import {
  Button,
  Form,
  FormItem,
  Input,
  TextArea,
  useForm,
  Message,
  ColorPicker,
  InputNumber,
  Checkbox,
} from "@/frontend/components/common";
import { disciplineService } from "@/frontend/services";
import { useNavigate } from "react-router-dom";
import type { CreateDisciplineRequestDTO, UpdateDisciplineRequestDTO, ApiResponse, DisciplineResponseDTO } from "@/shared/dto";
import { useEffect } from "react";

interface DisciplineFormValues {
  name: string;
  description?: string;
  color?: string;
  sortOrder: number;
  isSystem: boolean;
  canDelete: boolean;
  canEdit: boolean;
}

interface DisciplineFormProps {
  disciplineId?: string;
  initialValues?: Partial<DisciplineFormValues>;
}

export function DisciplineForm({ disciplineId, initialValues }: DisciplineFormProps) {
  const navigate = useNavigate();
  const [form] = useForm();
  const isEditMode = !!disciplineId;

  useEffect(() => {
    if (initialValues && isEditMode) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, isEditMode, form]);

  const handleSubmit = async (values: DisciplineFormValues) => {
    try {
      let response: ApiResponse<DisciplineResponseDTO>;

      if (isEditMode && disciplineId) {
        const updateData: UpdateDisciplineRequestDTO = {
          name: values.name,
          description: values.description || undefined,
          color: values.color || undefined,
          sortOrder: values.sortOrder,
          isSystem: values.isSystem,
          canDelete: values.canDelete,
          canEdit: values.canEdit,
        };
        response = await disciplineService.update(disciplineId, updateData);
      } else {
        const createData: CreateDisciplineRequestDTO = {
          name: values.name,
          description: values.description || undefined,
          color: values.color || undefined,
          sortOrder: values.sortOrder,
          isSystem: values.isSystem,
          canDelete: values.canDelete,
          canEdit: values.canEdit,
        };
        response = await disciplineService.create(createData);
      }

      if (response.success && response.data) {
        if (!isEditMode) {
          Message.success({ content: "Disciplina creada exitosamente" });
        } else {
          Message.success({ content: "Disciplina actualizada exitosamente" });
        }
        navigate("/disciplines");
      } else {
        Message.error({
          content: response.error || (isEditMode ? "Error al actualizar disciplina" : "Error al crear disciplina"),
        });
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : (isEditMode ? "Error al actualizar disciplina" : "Error al crear disciplina");

      if (errorMessage.toLowerCase().includes("nombre") || errorMessage.toLowerCase().includes("name")) {
        form.setFields([
          {
            name: "name",
            errors: [errorMessage],
          },
        ]);
      } else {
        Message.error({ content: errorMessage });
      }
    }
  };

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="vertical"
      className="w-full"
      initialValues={{
        description: "",
        sortOrder: 0,
        isSystem: false,
        canDelete: true,
        canEdit: true,
      }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormItem
          name="name"
          label="Nombre de la Disciplina"
          rules={[{ required: true, message: "El nombre es requerido" }]}
        >
          <Input
            placeholder="Ej: Fútbol, Gaming, Tenis de Mesa"
            size="large"
          />
        </FormItem>

        <FormItem
          name="description"
          label="Descripción"
          className="md:col-span-2"
        >
          <TextArea
            placeholder="Descripción de la disciplina (opcional)"
            rows={3}
            size="large"
          />
        </FormItem>

        <FormItem
          name="sortOrder"
          label="Orden de visualización"
          rules={[{ required: true, message: "El orden es requerido" }]}
        >
          <InputNumber
            placeholder="0"
            size="large"
            min={0}
            className="w-full"
          />
        </FormItem>

        <FormItem
          name="color"
          label="Color"
          className="md:col-span-2"
        >
          <ColorPicker
            format="hex"
          />
        </FormItem>

        <div className="md:col-span-2 flex flex-col gap-2">
          <FormItem name="isSystem" valuePropName="checked">
            <Checkbox label="Sistema (no editable por usuarios)" />
          </FormItem>
          <FormItem name="canDelete" valuePropName="checked">
            <Checkbox label="Permite eliminación" />
          </FormItem>
          <FormItem name="canEdit" valuePropName="checked">
            <Checkbox label="Permite edición" />
          </FormItem>
        </div>
      </div>

      <div className="flex justify-end gap-4 mt-6">
        <Button
          type="primary"
          htmlType="submit"
          size="large"
        >
          {isEditMode ? "Actualizar Disciplina" : "Crear Disciplina"}
        </Button>
        <Button
          size="large"
          onClick={() => navigate("/disciplines")}
        >
          Cancelar
        </Button>
      </div>
    </Form>
  );
}
